import { Box, Button, TextField } from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import { Music2 } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import {
  ExperienceFieldGuide,
  ExperienceFieldLabel,
  ExperienceHeader,
  ExperienceMediaCard,
  ExperienceOptionalLabel,
  ExperienceShell,
  type ExperienceMomentVariant,
  fieldHelperText,
  getExperienceFieldSx,
  getPrimaryButtonSx,
  isSeasonMomentVariant,
} from "./ExperienceUI";

interface InstrumentData {
  title?: string;
  url?: string | File;
  artistName?: string;
  description?: string;
}

interface InstrumentExperienceProps {
  darkMode: boolean;
  data?: InstrumentData;
  momentVariant?: ExperienceMomentVariant;
  onDataChange?: (data: InstrumentData) => void;
}

const isYoutubeUrl = (value?: string) => {
  if (!value) return false;
  try {
    const url = new URL(value);
    return (
      url.hostname.includes("youtube.com") || url.hostname.includes("youtu.be")
    );
  } catch {
    return false;
  }
};

const getYoutubeEmbedUrl = (value?: string) => {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.hostname.includes("youtu.be")) {
      const id = url.pathname.replace("/", "");
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (url.hostname.includes("youtube.com")) {
      const videoId = url.searchParams.get("v");
      if (videoId) return `https://www.youtube.com/embed/${videoId}`;
      const shortsMatch = url.pathname.match(/\/shorts\/([^/]+)/);
      if (shortsMatch) {
        return `https://www.youtube.com/embed/${shortsMatch[1]}`;
      }
      if (url.pathname.startsWith("/embed/")) {
        return value;
      }
    }
  } catch {
    return null;
  }
  return null;
};

const getValidationSchema = (t: any, isSeason: boolean) =>
  isSeason
    ? Yup.object({
        title: Yup.string().nullable(),
        artistName: Yup.string().nullable(),
        url: Yup.mixed<string | File>().test(
          "required",
          t("instrument_audio_required_season"),
          (value) => {
            if (value instanceof File) return true;
            return typeof value === "string" && value.trim() !== "";
          },
        ),
        description: Yup.string().nullable(),
      })
    : Yup.object({
    title: Yup.string()
      .required(t("title_is_required"))
      .min(3, t("title_must_be_at_least_3_characters")),
    artistName: Yup.string()
      .required(t("artist_name_is_required"))
      .min(2, t("artist_name_must_be_at_least_2_characters")),
    url: Yup.mixed<string | File>().test(
      "required",
      t("audio_is_required"),
      (value) => {
        if (value instanceof File) return true;
        return typeof value === "string" && value.trim() !== "";
      }
    ),
    description: Yup.string()
      .required(t("description_is_required"))
      .min(10, t("description_must_be_at_least_10_characters")),
  });

const InstrumentExperience: React.FC<InstrumentExperienceProps> = ({
  darkMode,
  data,
  momentVariant = "standard",
  onDataChange,
}) => {
  const { t } = useTranslation("taascard");
  const isSeason = isSeasonMomentVariant(momentVariant);
  const validationSchema = getValidationSchema(t, isSeason);
  const isInternalUpdateRef = useRef(false);
  const [playYoutubeAudio, setPlayYoutubeAudio] = useState(false);
  const fieldSx = getExperienceFieldSx(darkMode);
  const resolvedData = useMemo<InstrumentData>(
    () => ({
      title: data?.title ?? (isSeason ? "" : t("instrument_default_title")),
      url: data?.url ?? "",
      artistName: data?.artistName ?? "",
      description:
        data?.description ??
        (isSeason ? "" : t("instrument_default_description")),
    }),
    [data?.title, data?.url, data?.artistName, data?.description, isSeason, t],
  );

  const formik = useFormik<InstrumentData>({
    initialValues: resolvedData,
    enableReinitialize: true,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: !isSeason,
    onSubmit: () => {},
  });

  useEffect(() => {
    if (data && !isInternalUpdateRef.current) {
      const newValues: InstrumentData = resolvedData;
      const hasChanged = Object.keys(newValues).some(
        (key) =>
          formik.values[key as keyof InstrumentData] !==
          newValues[key as keyof InstrumentData]
      );
      if (hasChanged) {
        formik.setValues(newValues);
      }
    }
    isInternalUpdateRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, resolvedData]);

  useEffect(() => {
    if (!onDataChange) return;
    const hasChangedFromParent = (
      ["title", "url", "artistName", "description"] as Array<
        keyof InstrumentData
      >
    ).some((key) => formik.values[key] !== resolvedData[key]);
    if (!hasChangedFromParent) return;
    const timeoutId = setTimeout(() => {
      isInternalUpdateRef.current = true;
      onDataChange({
        title: formik.values.title,
        url: formik.values.url,
        artistName: formik.values.artistName,
        description: formik.values.description,
      });
    }, 0);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values, onDataChange, resolvedData]);

  const previewUrl = useMemo(() => {
    if (formik.values.url instanceof File) {
      return URL.createObjectURL(formik.values.url);
    }
    return typeof formik.values.url === "string" ? formik.values.url : "";
  }, [formik.values.url]);

  useEffect(() => {
    return () => {
      if (previewUrl && formik.values.url instanceof File) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl, formik.values.url]);

  const youtubeEmbedUrl = useMemo(
    () =>
      typeof formik.values.url === "string"
        ? getYoutubeEmbedUrl(formik.values.url)
        : null,
    [formik.values.url]
  );

  const handleFileSelect = (files: FileList | null) => {
    if (files && files.length > 0) {
      formik.setFieldValue("url", files[0]);
      formik.setFieldError("url", undefined);
      setPlayYoutubeAudio(false);
    }
  };

  const handleRemoveAudio = () => {
    formik.setFieldValue("url", "");
    formik.setFieldTouched("url", true);
    formik.setFieldError("url", t("audio_is_required"));
    setPlayYoutubeAudio(false);
  };

  const currentUrlLabel =
    formik.values.url instanceof File
      ? formik.values.url.name
      : typeof formik.values.url === "string"
        ? formik.values.url
        : "";

  return (
    <FormikProvider value={formik}>
      <ExperienceShell>
        {!isSeason && (
          <ExperienceHeader
            darkMode={darkMode}
            icon={Music2}
            title={t("instrument")}
            subtitle={t("instrument_header_subtitle")}
          />
        )}

        <ExperienceFieldGuide darkMode={darkMode}>
          {isSeason
            ? t("instrument_field_guide_season")
            : t("instrument_field_guide")}
        </ExperienceFieldGuide>

        <ExperienceFieldLabel darkMode={darkMode}>
          {t("instrument_audio_source")}
        </ExperienceFieldLabel>
        <TextField
          label={isSeason ? t("instrument_url_label_season") : t("instrument_url_label")}
          name="url"
          value={typeof formik.values.url === "string" ? formik.values.url : ""}
          onChange={(e) => {
            formik.setFieldValue("url", e.target.value);
            formik.setFieldError("url", undefined);
            setPlayYoutubeAudio(false);
          }}
          onBlur={() => formik.setFieldTouched("url", true)}
          fullWidth
          placeholder={t("instrument_url_placeholder")}
          error={Boolean(formik.touched.url && formik.errors.url)}
          helperText={fieldHelperText(
            formik.touched.url && formik.errors.url
              ? String(formik.errors.url)
              : undefined,
            formik.values.url instanceof File
              ? t("instrument_selected_file", { name: formik.values.url.name })
              : t("instrument_upload_helper"),
          )}
          sx={fieldSx}
        />

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <input
            type="file"
            accept="audio/*"
            id="instrument-audio-upload"
            hidden
            onChange={(e) => {
              handleFileSelect(e.target.files);
              e.target.value = "";
            }}
          />
          <label htmlFor="instrument-audio-upload">
            <Button variant="contained" component="span" sx={getPrimaryButtonSx(darkMode)}>
              {t("instrument_upload")}
            </Button>
          </label>
          {formik.values.url && (
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleRemoveAudio}
              sx={{ borderRadius: "999px", textTransform: "none" }}
            >
              {t("instrument_remove_audio")}
            </Button>
          )}
        </Box>

        {!!formik.values.url && (
          <ExperienceMediaCard darkMode={darkMode}>
            <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box
                component="span"
                sx={{
                  fontSize: "0.8rem",
                  color: "var(--neutral-500)",
                  wordBreak: "break-word",
                }}
              >
                {currentUrlLabel}
              </Box>
              {typeof formik.values.url === "string" &&
              isYoutubeUrl(formik.values.url) ? (
                <>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                      variant="contained"
                      onClick={() => setPlayYoutubeAudio(true)}
                      sx={getPrimaryButtonSx(darkMode)}
                    >
                      {t("instrument_play_youtube")}
                    </Button>
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={() => setPlayYoutubeAudio(false)}
                      sx={{ borderRadius: "999px", textTransform: "none" }}
                    >
                      {t("instrument_stop")}
                    </Button>
                  </Box>
                  {playYoutubeAudio && youtubeEmbedUrl && (
                    <iframe
                      src={`${youtubeEmbedUrl}?autoplay=1&controls=0&playsinline=1`}
                      title={t("instrument_audio_preview_title")}
                      allow="autoplay; encrypted-media"
                      style={{
                        width: 1,
                        height: 1,
                        border: 0,
                        opacity: 0,
                        position: "absolute",
                        pointerEvents: "none",
                      }}
                    />
                  )}
                </>
              ) : (
                <audio controls style={{ width: "100%" }}>
                  <source src={previewUrl} />
                  {t("instrument_browser_not_supported")}
                </audio>
              )}
            </Box>
          </ExperienceMediaCard>
        )}

        <TextField
          fullWidth
          multiline
          minRows={3}
          label={
            <>
              {isSeason ? t("instrument_caption_label_season") : t("instrument_caption_label")}
              {isSeason && <ExperienceOptionalLabel darkMode={darkMode} />}
            </>
          }
          name="description"
          value={formik.values.description}
          onChange={(e) => {
            formik.handleChange(e);
            formik.setFieldError("description", undefined);
          }}
          onBlur={formik.handleBlur}
          placeholder={t("instrument_description_placeholder")}
          error={Boolean(
            formik.touched.description && formik.errors.description,
          )}
          helperText={fieldHelperText(
            formik.touched.description && formik.errors.description
              ? String(formik.errors.description)
              : undefined,
            isSeason
              ? t("instrument_description_helper_season")
              : t("instrument_description_helper"),
          )}
          sx={fieldSx}
        />

        <TextField
          label={
            <>
              {t("instrument_track_title")}
              {isSeason && <ExperienceOptionalLabel darkMode={darkMode} />}
            </>
          }
          name="title"
          value={formik.values.title}
          onChange={(e) => {
            formik.handleChange(e);
            formik.setFieldError("title", undefined);
          }}
          onBlur={formik.handleBlur}
          fullWidth
          error={Boolean(formik.touched.title && formik.errors.title)}
          helperText={fieldHelperText(
            formik.touched.title && formik.errors.title
              ? String(formik.errors.title)
              : undefined,
            " ",
          )}
          sx={fieldSx}
        />

        <TextField
          label={
            <>
              {t("instrument_artist_name")}
              {isSeason && <ExperienceOptionalLabel darkMode={darkMode} />}
            </>
          }
          name="artistName"
          value={formik.values.artistName}
          onChange={(e) => {
            formik.handleChange(e);
            formik.setFieldError("artistName", undefined);
          }}
          onBlur={formik.handleBlur}
          fullWidth
          error={Boolean(formik.touched.artistName && formik.errors.artistName)}
          helperText={fieldHelperText(
            formik.touched.artistName && formik.errors.artistName
              ? String(formik.errors.artistName)
              : undefined,
            " ",
          )}
          sx={fieldSx}
        />
      </ExperienceShell>
    </FormikProvider>
  );
};

export default InstrumentExperience;
